import { useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import BackHeader from '../../components/BackHeader/BackHeader'
import { downloadAppleCalendar, eventCalendarEvent, hasStarted, initialsFrom, openGoogleCalendar } from './logic'
import { useHold } from './store'
import Loading from '../../components/Loading/Loading'
import TextCard from './components/TextCard'
import {
  ArchiveNote,
  Composer,
  DateTimeField,
  HoldScreen,
  PlaceLink,
  SeasonSwitch,
  SignupButton,
  SpotLine,
  SquadNeed,
  When,
} from './bits'

const calendarDialogPaper = {
  className: 'hold-create-paper hold-notice-paper',
  sx: {
    backgroundColor: '#221c24',
    color: '#f3eadf',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '18px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
  },
}

const calendarDialogBackdrop = {
  sx: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
}

export default function EventViews({ list = false }) {
  if (list) return <EventList />
  return <EventDetail />
}

function EventList() {
  const navigate = useNavigate()
  const { team, season, isAdmin, dispatch, loading } = useHold()
  const [open, setOpen] = useState(false)
  if (loading && !team) return <HoldScreen><Loading fill /></HoldScreen>
  if (!team || !season) return null
  const events = [...season.events].sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff))

  return (
    <HoldScreen>
      <BackHeader eyebrow={team.name} title="Arrangementer" subtitle={season.name} onBack={() => navigate(`/hold/${team.id}`)} />
      <SeasonSwitch team={team} season={season} dispatch={dispatch} />
      <ArchiveNote season={season} />
      {isAdmin && season.current ? (
        <button type="button" className="hold-action hold-action--ghost" onClick={() => setOpen((value) => !value)}>
          {open ? 'Luk' : 'Nyt arrangement'}
        </button>
      ) : null}
      {open ? <EventForm teamId={team.id} seasonId={season.id} dispatch={dispatch} onDone={() => setOpen(false)} /> : null}
      <div className="hold-stack">
        {events.length === 0 ? <p className="hold-empty">Ingen arrangementer i sæsonen.</p> : events.map((event) => (
          <Link key={event.id} className="hold-card hold-link-card" to={`/hold/${team.id}/arrangement/${event.id}`}>
            <div className="hold-card-top">
              <strong>{event.title}</strong>
              <SpotLine item={event} />
            </div>
            <p className="hold-meta"><When iso={event.kickoff} /></p>
            <p className="hold-meta">{event.place}</p>
          </Link>
        ))}
      </div>
    </HoldScreen>
  )
}

function EventForm({ teamId, seasonId, dispatch, onDone }) {
  const [title, setTitle] = useState('')
  const [kickoff, setKickoff] = useState('')
  const [place, setPlace] = useState('')
  const [description, setDescription] = useState('')
  const [max, setMax] = useState('')
  const ready = Boolean(title.trim() && kickoff && place.trim())

  return (
    <form
      className="hold-form"
      onSubmit={(event) => {
        event.preventDefault()
        if (!title.trim() || !kickoff || !place.trim()) return
        dispatch({
          type: 'ADD_EVENT',
          teamId,
          seasonId,
          event: {
            title: title.trim(),
            kickoff: new Date(kickoff).toISOString(),
            place: place.trim(),
            description: description.trim(),
            max: max === '' ? null : Number(max),
          },
        })
        onDone()
      }}
    >
      <label>Titel<input value={title} onChange={(event) => setTitle(event.target.value)} /></label>
      <label>Tidspunkt<DateTimeField value={kickoff} onChange={(event) => setKickoff(event.target.value)} /></label>
      <label>Sted<input value={place} onChange={(event) => setPlace(event.target.value)} /></label>
      <label>Beskrivelse<textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} /></label>
      <label>Max antal, tomt hvis der ikke er et loft<input type="number" min="1" value={max} onChange={(event) => setMax(event.target.value)} /></label>
      <button type="submit" className="hold-action hold-action--save" disabled={!ready}>Gem arrangement</button>
    </form>
  )
}

function eventFace(iso) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return { weekday: '', day: '–', month: '', time: '–' }
  return {
    weekday: new Intl.DateTimeFormat('da-DK', { weekday: 'long' }).format(date),
    day: new Intl.DateTimeFormat('da-DK', { day: 'numeric' }).format(date),
    month: new Intl.DateTimeFormat('da-DK', { month: 'short' }).format(date),
    time: new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date),
  }
}

function EventPoster({ event, onAddCalendar }) {
  const face = eventFace(event.kickoff)
  const past = hasStarted(event.kickoff)
  return (
    <section className={`hold-fixture hold-event${past ? ' is-past' : ''}`}>
      <div className="hold-event-top">
        <div className="hold-event-date" aria-hidden="true">
          <span>{face.month}</span>
          <strong>{face.day}</strong>
        </div>
        <div className="hold-event-when">
          <span>{face.weekday}</span>
          <strong>{face.time}</strong>
          <SpotLine item={event} />
        </div>
      </div>
      {event.place ? <p className="hold-fixture-place"><span>{event.place}</span></p> : null}
      {event.place || onAddCalendar ? (
        <div className="hold-fixture-actions">
          {event.place ? <PlaceLink place={event.place} button /> : null}
          {onAddCalendar ? (
            <button type="button" className="hold-fixture-cal" aria-label="Tilføj til kalender" onClick={onAddCalendar}>
              <CalendarMonthOutlinedIcon />
            </button>
          ) : null}
        </div>
      ) : null}
      <SquadNeed count={event.attendees.length} max={event.max} min={0} />
    </section>
  )
}

function EventAbout({ text }) {
  const body = useRef(null)
  const [open, setOpen] = useState(false)
  const [overflows, setOverflows] = useState(false)

  useLayoutEffect(() => {
    const node = body.current
    if (!node) return
    if (open) return
    setOverflows(node.scrollHeight > node.clientHeight + 1)
  }, [text, open])

  return (
    <section className="hold-event-about">
      <h2>Om arrangementet</h2>
      <p ref={body} className={open ? 'is-open' : ''}>{text}</p>
      {overflows || open ? (
        <button type="button" className="hold-text-button" onClick={() => setOpen((value) => !value)}>
          {open ? 'Vis mindre' : 'Vis mere'}
        </button>
      ) : null}
    </section>
  )
}

function EventRoster({ title, ids, nameOf, empty }) {
  return (
    <section className="hold-stack">
      <h2>{title}</h2>
      {ids.length === 0 ? <p className="hold-empty">{empty}</p> : (
        <ul className="hold-event-roster">
          {ids.map((id) => {
            const name = nameOf(id)
            return (
              <li key={id}>
                <span className="hold-fine-avatar" aria-hidden="true">{initialsFrom(name)}</span>
                <strong>{name}</strong>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function EventDetail() {
  const navigate = useNavigate()
  const { eventId } = useParams()
  const { team, dispatch, nameOf, meId, loading } = useHold()
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [declinedOpen, setDeclinedOpen] = useState(false)
  if (loading && !team) return <HoldScreen><Loading fill /></HoldScreen>
  if (!team) return null
  const found = team.seasons
    .map((item) => ({ season: item, event: item.events.find((entry) => entry.id === eventId) }))
    .find((item) => item.event)
  if (!found) return <HoldScreen><p className="hold-empty">Arrangementet findes ikke.</p></HoldScreen>
  const { season, event } = found
  const calendarEvent = eventCalendarEvent(team, event)

  return (
    <HoldScreen>
      <BackHeader eyebrow={team.name} title={event.title} subtitle={season.name} onBack={() => navigate(`/hold/${team.id}/arrangementer`)} />
      <EventPoster event={event} onAddCalendar={calendarEvent ? () => setCalendarOpen(true) : null} />
      {event.description ? <EventAbout text={event.description} /> : null}
      <Dialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={calendarDialogPaper}
        BackdropProps={calendarDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Tilføj til kalender</DialogTitle>
        <DialogContent>
          <p className="hold-copy">Vælg hvilken kalender arrangementet skal lande i.</p>
          <div className="hold-match-actions">
            <button
              type="button"
              className="hold-action"
              onClick={() => {
                downloadAppleCalendar(calendarEvent)
                setCalendarOpen(false)
              }}
            >
              Apple Kalender
            </button>
            <button
              type="button"
              className="hold-action"
              onClick={() => {
                openGoogleCalendar(calendarEvent)
                setCalendarOpen(false)
              }}
            >
              Google Kalender
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <SignupButton
        item={event}
        season={season}
        declineLabel="Kan ikke"
        declinedText="Du har meldt, at du ikke kan"
        onToggle={() => {
          const joined = event.attendees.includes(meId) || event.waitlist.includes(meId)
          const declined = (event.declined || []).includes(meId)
          dispatch({
            type: 'SET_EVENT_REPLY',
            teamId: team.id,
            eventId: event.id,
            reply: joined && !declined ? 'clear' : 'yes',
          })
        }}
        onDecline={() => dispatch({ type: 'SET_EVENT_REPLY', teamId: team.id, eventId: event.id, reply: 'no' })}
      />
      <EventRoster
        title={`Tilmeldte (${event.attendees.length}${event.max != null ? ` / ${event.max}` : ''})`}
        ids={event.attendees}
        nameOf={nameOf}
        empty="Ingen er meldt til endnu."
      />
      {event.max != null ? (
        <EventRoster title="Venteliste" ids={event.waitlist} nameOf={nameOf} empty="Ingen på venteliste." />
      ) : null}
      <button type="button" className="hold-action hold-action--ghost" onClick={() => setDeclinedOpen(true)}>
        Se afbud ({(event.declined || []).length})
      </button>
      <Dialog
        open={declinedOpen}
        onClose={() => setDeclinedOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={calendarDialogPaper}
        BackdropProps={calendarDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Afbud</DialogTitle>
        <DialogContent>
          {(event.declined || []).length === 0 ? <p className="hold-empty">Ingen har meldt afbud.</p> : (
            <ul className="hold-event-roster">
              {(event.declined || []).map((id) => {
                const name = nameOf(id)
                return (
                  <li key={id}>
                    <span className="hold-fine-avatar" aria-hidden="true">{initialsFrom(name)}</span>
                    <strong>{name}</strong>
                  </li>
                )
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
      <section className="hold-stack">
        <h2>Arrangementets væg</h2>
        {(event.messages || []).length === 0 ? <p className="hold-empty">Ingen beskeder endnu.</p> : (event.messages || []).map((message) => (
          <div key={message.id} className="hold-stack">
            <TextCard
              text={message.text}
              image={message.image}
              allowImage
              meta={message.authorName || nameOf(message.authorId)}
              at={message.createdAt}
              canEdit={message.authorId === meId}
              mine={message.authorId === meId}
              like={{
                count: (message.likedBy || []).length,
                people: [...(message.likedBy || [])]
                  .map((id) => ({ id, name: nameOf(id) }))
                  .sort((a, b) => a.name.localeCompare(b.name, 'da')),
                on: (message.likedBy || []).includes(meId),
                onToggle: message.authorId === meId
                  ? null
                  : () => dispatch({ type: 'TOGGLE_MATCH_LIKE', teamId: team.id, messageId: message.id }),
              }}
              onSave={(text, image) => dispatch({
                type: 'UPDATE_MATCH_MESSAGE',
                teamId: team.id,
                messageId: message.id,
                text,
                image,
              })}
              onDelete={() => dispatch({
                type: 'DELETE_MATCH_MESSAGE',
                teamId: team.id,
                messageId: message.id,
              })}
              canComment
              onCommenting={(text) => dispatch({ type: 'ADD_MATCH_COMMENT', teamId: team.id, messageId: message.id, text })}
            />
            <div className="hold-comments">
              {(message.comments || []).map((comment) => (
                <TextCard
                  key={comment.id}
                  compact
                  text={comment.text}
                  meta={comment.authorName || nameOf(comment.authorId)}
                  at={comment.createdAt}
                  canEdit={comment.authorId === meId}
                  mine={comment.authorId === meId}
                  onSave={(text) => dispatch({ type: 'UPDATE_COMMENT', teamId: team.id, commentId: comment.id, text })}
                  onDelete={() => dispatch({ type: 'DELETE_COMMENT', teamId: team.id, commentId: comment.id })}
                />
              ))}
            </div>
          </div>
        ))}
        <Composer
          allowImage
          placeholder="Skriv på arrangementets væg"
          onSubmit={(text, image) => dispatch({
            type: 'ADD_EVENT_MESSAGE',
            teamId: team.id,
            eventId: event.id,
            text,
            image,
          })}
        />
      </section>
    </HoldScreen>
  )
}
